import { makeStyles } from "tss-react/mui";
import { MiChip, MiSkeleton, SKELETON_VARIANTS } from "@miplatform/components";
import { Box, List, ListItemButton, ListItemText } from "@mui/material";
import { FilterSidebarInput } from "../../../../components/FilterSidebar/FilterSidebar.types";
import { DynamicListFilterOption } from "./DynamicListFilter.types";
import { useDynamicListFilter } from "./useDynamicListFilter";

const LOADING_ROWS = [1, 2, 3, 4, 5];

/**
 * Renders a vertical list of filters driven by the given options, i.e.
 *
 * {
 *   label: string;
 *   value: string;
 *   amount: number;
 *   filter: {
 *     key: string; i.e. assigneeId, planStatus, communityId, etc.
 *     value: string; i.e. "eq exampleGuid" or "ne null" or "eq EnumValue"
 *   }
 *   children: [] // array of child filter objects with the same structure
 * }[]
 *
 * Selecting an option applies its filter criteria (together with the criteria
 * of its parent) through `useFilterSidebar`.
 */
export function DynamicListFilter<FilterType extends FilterSidebarInput>({
  options,
  filters,
  initialFilters,
  onApplyFilters,
  enableSearchParamsSync = false,
  loading = false,
  width,
}: Readonly<DynamicListFilterProps<FilterType>>) {
  const { classes } = useStyles({ width });
  const displayOptions = options.some((option) => option.value === "all")
    ? options
    : [
        {
          label: "All",
          value: "all",
          amount: options.reduce(
            (total, option) => total + (option.amount || 0),
            0
          ),
        },
        ...options,
      ];

  const { selectedOption, selectOption } = useDynamicListFilter<FilterType>({
    options,
    filters,
    initialFilters,
    enableSearchParamsSync,
    onApplyFilters,
  });

  if (loading) {
    return (
      <Box
        className={classes.sidebar}
        data-testid="dynamic-list-filter-loading"
      >
        {LOADING_ROWS.map((row) => (
          <MiSkeleton
            key={row}
            variant={SKELETON_VARIANTS.RECTANGULAR}
            height={32}
            className={classes.loadingRow}
          />
        ))}
      </Box>
    );
  }

  return (
    <List
      component="nav"
      className={classes.sidebar}
      data-testid="dynamic-list-filter"
      disablePadding
    >
      {displayOptions.map((option) => (
        <Box key={option.value}>
          <FilterRow
            label={option.label}
            amount={option.amount}
            isSelected={
              selectedOption.value === option.value &&
              selectedOption.parentValue === null
            }
            isParent
            onSelect={() => selectOption(option)}
            testId={`dynamic-list-filter-option-${option.value}`}
          />
          {option.children?.map((child) => (
            <FilterRow
              key={child.value}
              label={child.label}
              amount={child.amount}
              isSelected={
                selectedOption.value === child.value &&
                selectedOption.parentValue === option.value
              }
              onSelect={() => selectOption(child, [option])}
              testId={`dynamic-list-filter-option-${child.value}`}
            />
          ))}
        </Box>
      ))}
    </List>
  );
}

type FilterRowProps = {
  label: string;
  amount?: number;
  isSelected: boolean;
  isParent?: boolean;
  onSelect: () => void;
  testId: string;
};

function FilterRow({
  label,
  amount,
  isSelected,
  isParent = false,
  onSelect,
  testId,
}: Readonly<FilterRowProps>) {
  const { classes, theme, cx } = useStyles({});

  return (
    <ListItemButton
      selected={isSelected}
      onClick={onSelect}
      data-testid={testId}
      className={cx(isParent ? classes.parentItem : classes.nestedItem, {
        [classes.selectedStyle]: isSelected,
      })}
    >
      <Box className={classes.row}>
        <ListItemText
          primary={label}
          slotProps={{
            primary: {
              fontSize: theme.fontSize.small,
              fontWeight: isParent
                ? theme.fontWeight.bold
                : theme.fontWeight.regular,
            },
          }}
        />
        {amount && amount > 0 ? (
          <MiChip
            size="small"
            className={classes.amountBadge}
            label={String(amount)}
          />
        ) : null}
      </Box>
    </ListItemButton>
  );
}

export interface DynamicListFilterProps<FilterType extends FilterSidebarInput> {
  options: DynamicListFilterOption[];
  filters: FilterType;
  initialFilters: FilterType;
  onApplyFilters: (filters: FilterType, queryUrl?: string) => void;
  enableSearchParamsSync?: boolean;
  loading?: boolean;
  width?: number;
}

const useStyles = makeStyles<{ width?: number }>()((theme, { width }) => ({
  sidebar: {
    minWidth: width || 220,
    maxWidth: width,
    overflowY: "auto",
  },
  parentItem: {
    padding: theme.spacing(1.5, 3, 1.5, 1.5),
    borderRadius: 4,
  },
  nestedItem: {
    padding: theme.spacing(1.5, 3),
    borderRadius: 4,
  },
  selectedStyle: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: theme.palette.primary.light,
    },
    "&.Mui-selected": {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.main,
    },
  },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    flex: 1,
    minWidth: 0,
    "& .MuiListItemText-root": {
      flex: "1 1 auto",
      minWidth: 0,
    },
  },
  amountBadge: {
    alignItems: "center",
    justifyContent: "center",
    fontWeight: theme.fontWeight.regular,
    height: "auto",
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
    "& .MuiChip-label": {
      fontSize: 11,
    },
  },
  loadingRow: {
    borderRadius: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
}));
